import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { SkuSearchDto, SkuSearchRequest } from '@emi/shared/models';
import { SkuSearchService } from '@emi/shared/data-access';
import { Paging, SkuSearchFields } from '@emi/shared/constants';
import { DataExtractionState, getSelectedFilters } from '@emi/shared/state/filters';
import * as SkuSearchActions from './sku-search.actions';
import { SharedStateSkuSearchModule } from './shared-state-sku-search.module';
import { selectSkuSearchState } from './sku-search.selectors';

@Injectable()
export class SkuSearchEffects {
    constructor(
        private actions$: Actions,
        private skuSearchService: SkuSearchService,
        private dataExtractionStore: Store<DataExtractionState>,
        private skuSearchStore: Store<SharedStateSkuSearchModule>
    ) {}

    getSkuSearchResults$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SkuSearchActions.getSkuSearchResults),
            concatLatestFrom(() => [ this.skuSearchStore.select(selectSkuSearchState), this.dataExtractionStore.select(getSelectedFilters) ]),
            switchMap(([{ searchTerm, searchOption, sortBy, orderBy, includeSearchPhrase }, { currency }, filters]) => {           
                const request = SkuSearchRequest.from({
                    filters,
                    resultsPerPage: Paging.SKU_SEARCH_PAGE_SIZE,
                    searchPhrase:  includeSearchPhrase ? searchTerm : '',
                    fields: SkuSearchFields,
                    currency: currency,
                    searchType: searchOption.label,
                    sortBy: sortBy,
                    orderBy: orderBy
                });
                return this.skuSearchService.getSkus(request).pipe(
                    map((data: SkuSearchDto) => {
                        return SkuSearchActions.getSkuSearchResultsSuccess({
                            results: data.results,
                            skuCount: data.metadata.totalResults,
                            nextPageId: data.metadata.nextPageId || '',
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error.status == 404) {
                            return of(
                                SkuSearchActions.getSkuSearchResultsSuccess({
                                    results: [],
                                    skuCount: 0,
                                    nextPageId: '',
                                })
                            );
                        }
                        return of(SkuSearchActions.getSkuSearchResultsFailure({ error }));
                    })
                );
            })
        )
    );

    getNextPageSkuSearchResults$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SkuSearchActions.getNextPageSkuSearchResults),
            concatLatestFrom(() => [this.skuSearchStore.select(selectSkuSearchState), this.dataExtractionStore.select(getSelectedFilters)]),
            switchMap(([, { searchTerm, currency, pageIds, sortBy, orderBy, includeSearchPhrase }, filters]) => {
                const request = SkuSearchRequest.from({
                    filters,
                    resultsPerPage: Paging.SKU_SEARCH_PAGE_SIZE,
                    searchPhrase: includeSearchPhrase ? searchTerm : '',
                    fields: SkuSearchFields,
                    currency: currency,
                    sortBy: sortBy,
                    orderBy: orderBy,
                    pageId: pageIds[pageIds.length - 1],
                });

                return this.skuSearchService.getSkus(request).pipe(
                    map((data: SkuSearchDto) => {
                        return SkuSearchActions.updateSkuSearchResultsSuccess({
                            results: data.results,
                            nextPageId: data.metadata.nextPageId || '',
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error.status == 404) {
                            return of(
                                SkuSearchActions.updateSkuSearchResultsSuccess({
                                    results: [],
                                    nextPageId: '',
                                })
                            );
                        }
                        return of(SkuSearchActions.getSkuSearchResultsFailure({ error }));
                    })
                );
            })
        )
    );

    getSkuCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SkuSearchActions.getSkuCount),
            concatLatestFrom(() => this.dataExtractionStore.select(getSelectedFilters)),
            switchMap(([, filters]) => {
                const request = SkuSearchRequest.from({ filters, resultsPerPage: 1, fields: ['Name'] });

                return this.skuSearchService.getSkus(request).pipe(
                    map((data: SkuSearchDto) => {
                        return SkuSearchActions.getSkuCountSuccess({ skuCount: data.metadata.totalResults });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error.status == 404) {
                            return of(SkuSearchActions.getSkuCountSuccess({ skuCount: 0 }));
                        }
                        return of(SkuSearchActions.getSkuCountFailure({ error }));
                    })
                );
            })
        )
    );            
}
